import queryString from 'query-string';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { PaginationI } from '~src/interfaces/pagination.interface';
import { FetchScalesParams, Scale, ScaleFilterTypes, ScalesRespI } from '~src/interfaces/scale.interface';
import { fetchScales } from '~src/modules/scales/actions/scaleActions';
import { filterOptions, ScalesFilter } from '~src/modules/scales/constants/filterOptions';
import { StoreI } from '~src/rootReducer';
import { routes } from '~src/routingConfig';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import logger from '~src/services/logger/LlsLogger';
import { translateContent } from '~src/services/translationServices';
import TextBtnPageFooter from '~src/shared/footers/textBtnPageFooter/TextBtnPageFooter';
import ListPageHeader from '~src/shared/headers/listPageHeader/ListPageHeader';
import ItemsAmount from '~src/shared/lists/itemsAmount/ItemsAmount';
import ListAddButton from '~src/shared/lists/listAddButton/ListAddButton';
import ListFilters from '~src/shared/lists/listFilters/ListFilters';
import Pagination from '~src/shared/lists/pagination/Pagination';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import { MainContainerAnimation } from '~src/styles/animations';
import { ListMargin, PageContentContainer } from '~src/styles/containers.sc';

import ScaleItem from './ScaleItem';
import { ItemsAmountRow, ScalesContainer } from './scalesList.sc';

interface SearchStrObjI {
  page?: number;
  filterSubject?: ScaleFilterTypes;
}

interface StateProps {
  scales: Scale[];
  pagination: PaginationI | null;
  loadingScales: boolean;
}

interface DispatchProps {
  fetchScales(params: SearchStrObjI): ApiReduxResp<ScalesRespI>;
}

type Props = StateProps & DispatchProps & WithTranslation & RouteComponentProps<any>;

interface State {
  filters: ScalesFilter[];
  curFilter: ScaleFilterTypes | undefined;
  initialPage: number;
  isChangingFilter: boolean;
}

class ScalesList extends React.Component<Props, State> {
  isUnmounted = false;

  constructor(props: Props) {
    super(props);
    const searchStrObj: SearchStrObjI = queryString.parse(props.location.search) || {};
    this.state = {
      filters: [...filterOptions],
      curFilter: searchStrObj.filterSubject,
      initialPage: Number(searchStrObj.page) || 1,
      isChangingFilter: false,
    };
  }

  componentDidMount() {
    void this.fetchScales({
      page: this.state.initialPage,
      'filter.visualization': this.state.curFilter,
    }).then((response) => {
      if (!this.isUnmounted && response && response.data) {
        this.updateFiltersState(response.data);
      }
    });
  }

  componentWillUnmount() {
    this.isUnmounted = true;
  }

  fetchScales = (params: FetchScalesParams) => {
    const filterParam = params['filter.visualization'] || this.state.curFilter;

    this.setState({ isChangingFilter: !!params['filter.visualization'] });
    if (!filterParam || filterParam === 'all') {
      delete params['filter.visualization'];
    }
    return this.props
      .fetchScales(params)
      .then(({ error, response }) => {
        if (error) {
          throw new Error('Error fetching Scales');
        }
        if (!this.isUnmounted) {
          this.setState({ isChangingFilter: false });
        }
        return response;
      })
      .catch((error) => {
        logger.info('Error fetching Scales', {
          error,
          info: {
            componentStack: 'ScalesList.jsx',
          },
        });
      });
  };

  goToAdd = () => {
    this.props.history.push(routes.contentScales.scalesAdd);
  };

  updateFiltersState = ({ scales_distribution }: ScalesRespI) => {
    this.setState((prevState) => {
      const mappedFilters = prevState.filters.map((filter) => {
        let itemsAmount: number | undefined;
        if (filter.id !== 'all') {
          itemsAmount = scales_distribution[filter.id];
          filter.disabled = !itemsAmount;
        }
        return {
          ...filter,
          itemsAmount,
        };
      });
      return { filters: mappedFilters };
    });
  };

  applyFilter = (i: number) => {
    const curFilter = this.state.filters[i].id;
    this.setState({ curFilter });
    void this.fetchScales({ 'filter.visualization': curFilter });
    this.props.history.replace({ search: curFilter !== 'all' ? `filterSubject=${curFilter}` : '' });
  };

  getScalesByPage = (page: number) => this.fetchScales({ page });

  render() {
    const { t, scales, loadingScales, pagination } = this.props;

    return (
      <main>
        <ListPageHeader
          t={t}
          titleTxt="x.scales.list.header.title"
          subTitleTxt="x.scales.list.header.subtitle"
        />
        <PageContentContainer>
          <ListMargin>
            <ListFilters
              filterOptions={this.state.filters}
              doFilter={this.applyFilter}
              initialFilter={this.state.curFilter}
            />
            <ItemsAmountRow>
              {pagination && !loadingScales && (
                <ItemsAmount
                  pagination={pagination}
                  onePageKey="x.scales.list.scales_amount"
                  multyPageKey="x.scales.list.scales_amount_from_to"
                />
              )}
            </ItemsAmountRow>
            <ListAddButton
              addFunc={this.goToAdd}
              buttonText={{
                key: 'x.scales.list.add.button',
                txt: t('x.scales.list.add.button'),
              }}
            />
            {loadingScales ? (
              <LoadingSpinner marginTop={60} />
            ) : (
              <MainContainerAnimation>
                <ScalesContainer>
                  {scales.map((scale) => (
                    <ScaleItem
                      key={scale.id}
                      scaleName={translateContent(scale.name)}
                      visualization={scale.visualization}
                      labels={scale.labels}
                      link={`${this.props.match.url}/${scale.id}`}
                    />
                  ))}
                </ScalesContainer>
              </MainContainerAnimation>
            )}
            {pagination && !this.state.isChangingFilter && (
              <Pagination
                t={t}
                totalItems={pagination.total}
                pageSize={pagination.per_page}
                onChange={this.getScalesByPage}
                marginTop={60}
                history={this.props.history}
                location={this.props.location}
                defaultCurrPage={this.state.initialPage}
              />
            )}
            <TextBtnPageFooter
              t={t}
              infoKey="x.scales.list.footer.info_text"
              btnKey="x.scales.list.footer.button_text"
              linkUrl={routes.contentTopics.topics}
            />
          </ListMargin>
        </PageContentContainer>
      </main>
    );
  }
}

const mapStateToProps = ({ scales }: StoreI): StateProps => ({
  scales: scales.items,
  pagination: scales.pagination,
  loadingScales: scales.loadingScales,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  fetchScales: (params) => dispatch(fetchScales(params)),
});

const ScalesListT = withTranslation()(ScalesList);
const ScalesListC = connect(mapStateToProps, mapDispatchToProps)(ScalesListT);

export default ScalesListC;
